import { useParams, useNavigate } from "react-router-dom";
import { useEffect } from "react";
import { Package, MapPin, Users, ArrowRight, Sparkles } from "lucide-react";
import useActivity from "@/hooks/useActivity";
import useSeo from "@/hooks/useSeo";
import { useApp } from "@/context/AppContext";
import Topography from "@/components/Topography";
import { LoadingState, ErrorState, NotFoundState } from "@/components/common/States";

function StyleCard({ testid, icon: Icon, name, tagline, onClick }) {
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      className="group text-left rounded-3xl bg-white border border-brand-navy/10 pib-shadow p-6 hover:border-brand-orange transition-all hover:-translate-y-1 h-full flex flex-col"
    >
      <div className="w-12 h-12 rounded-2xl bg-brand-orange/12 flex items-center justify-center mb-3 group-hover:bg-brand-orange transition-colors">
        <Icon className="w-6 h-6 text-brand-orange group-hover:text-white transition-colors" />
      </div>
      <h3 className="display-title text-2xl text-brand-navy">{name}</h3>
      <p className="mt-2 text-brand-navy/70 leading-snug flex-1">{tagline}</p>
      <span className="mt-4 inline-flex items-center gap-1.5 text-brand-orange font-semibold">
        Open this guide <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
      </span>
    </button>
  );
}

export default function OrganizerHub() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { activity, status } = useActivity(slug);
  const { role, setRole } = useApp();
  useSeo("Organizer Guide", "Welcome, Organizer! Choose your event style and follow the guide to set up, run, and wrap up your event.");

  // Landing here implies the organizer context.
  useEffect(() => {
    if (role !== "organizer") setRole("organizer");
  }, [role, setRole]);

  if (status === "loading") return <LoadingState />;
  if (status === "notfound") return <NotFoundState />;
  if (status === "error" || !activity) return <ErrorState onRetry={() => window.location.reload()} />;

  const modes = activity.modes;

  return (
    <div>
      {/* Welcome hero */}
      <section className="relative overflow-hidden bg-brand-orange text-white">
        <Topography color="#F5F0E1" opacity={0.12} />
        <div className="relative mx-auto max-w-5xl px-4 sm:px-6 py-10 sm:py-14">
          <span className="inline-flex items-center gap-2 rounded-full bg-white/20 px-3 py-1 text-sm font-semibold">
            <Sparkles className="w-4 h-4" /> {activity.name}
          </span>
          <h1 className="display-title text-3xl sm:text-5xl mt-4">Welcome, Organizer!</h1>
          <p className="mt-3 max-w-2xl text-white/90 text-lg">
            You're the reason this event is happening. Everything you need is already in your kit - let's set it up together.
          </p>
        </div>
      </section>

      <div className="mx-auto max-w-5xl px-4 sm:px-6 py-8">
        {/* Organizer Kit strip */}
        <section className="rounded-2xl bg-brand-cream border border-brand-cream-dark p-5 sm:p-6">
          <div className="flex items-center gap-2 mb-4">
            <Package className="w-5 h-5 text-brand-orange" />
            <h2 className="display-title text-xl text-brand-navy">What's in your Organizer Kit</h2>
          </div>
          <div className="grid gap-3 sm:grid-cols-3">
            {activity.ambassadorKit.contents.map((c, i) => (
              <div key={i} className="rounded-xl bg-white p-4 border border-brand-navy/10" data-testid={`organizer-kit-pouch-${i}`}>
                <p className="font-semibold text-brand-navy">{c.title}</p>
                {c.bullets && c.bullets.length > 0 ? (
                  <ul className="mt-1 space-y-1 text-sm text-brand-navy/70 leading-snug list-disc pl-5">
                    {c.bullets.map((b, bi) => (
                      <li key={bi}>{b}</li>
                    ))}
                  </ul>
                ) : (
                  c.description && <p className="mt-1 text-sm text-brand-navy/70 leading-snug">{c.description}</p>
                )}
              </div>
            ))}
          </div>
        </section>

        {/* Style chooser */}
        <section className="mt-8">
          <h2 className="display-title text-2xl text-brand-navy">Choose your event style</h2>
          <p className="mt-1 text-brand-navy/70">Two ways to run it - pick what fits your team.</p>
          <div className="mt-4 grid gap-4 sm:grid-cols-2">
            <StyleCard
              testid="style-drop-in"
              icon={MapPin}
              name={modes["drop-in"].name}
              tagline={modes["drop-in"].tagline}
              onClick={() => navigate(`/activities/${slug}/organizer/drop-in`)}
            />
            <StyleCard
              testid="style-self-hosted"
              icon={Users}
              name={modes["self-hosted"].name}
              tagline={modes["self-hosted"].tagline}
              onClick={() => navigate(`/activities/${slug}/organizer/self-hosted`)}
            />
          </div>
          <p className="mt-4 rounded-xl bg-brand-navy/5 px-4 py-3 text-sm text-brand-navy/80">
            Your kit supports both styles. You can decide now or after looking at both guides.
          </p>
        </section>
      </div>
    </div>
  );
}
